import { z } from "zod";

/**
 * Wire-shape schemas for the RevenueCat-backed react-query caches.
 *
 * Unlike the trpc-backed caches in `functions/api/types/*`, these are *not*
 * tRPC route outputs — they're SDK return shapes from `react-native-purchases`
 * on native and web. The SDK returns complex objects with versioned fields we
 * don't enumerate; `passthrough()` keeps unknown keys around so a SDK upgrade
 * doesn't poison the cache by stripping fields the app actually reads.
 *
 * Used by `apps/mobile/utils/revenuecat.ts` to durably cache the monthly
 * offering and the user's premium-entitlement boolean. Premium-status gating
 * (ads, paywalls, skip-limits) resolves from SQLite instantly on cold start
 * instead of waiting for the RevenueCat SDK round-trip.
 */

/**
 * `["isPremium"]` — boolean entitlement flag derived from
 * `Purchases.getCustomerInfo().entitlements.active.Premium != null`. Drives
 * paywalls, ad-frequency, and skip-limits in the player runtime.
 */
export const isPremiumSchema = z.boolean();
export type IsPremium = z.infer<typeof isPremiumSchema>;

/**
 * `["monthlyOffering"]` — the current monthly `PurchasesPackage` returned by
 * `Purchases.getOfferings().current.monthly`, or `null` when the user has no
 * monthly offering available. The SDK returns third-party-versioned objects
 * with nested product info; we keep the schema permissive (`passthrough` +
 * `nullable`) so the cache survives SDK shape changes — only the
 * existence/null contract is enforced.
 */
export const monthlyOfferingSchema = z
  .object({})
  .passthrough()
  .nullable();
export type MonthlyOffering = z.infer<typeof monthlyOfferingSchema>;

/**
 * `["trialInfo"]` — trial-period data derived from the Premium entitlement's
 * `periodType` and `expirationDate`. `null` when the user has no active
 * Premium entitlement or RevenueCat is unavailable.
 */
export const trialInfoSchema = z
  .object({
    periodType: z.string(),
    expirationDate: z.string().nullable(),
  })
  .nullable();
export type TrialInfo = z.infer<typeof trialInfoSchema>;
